import React, { Suspense, lazy } from 'react';
import { ConfigProvider, Layout, Spin, FloatButton, App as AntApp } from 'antd';
import { LoadingOutlined, SmileOutlined } from '@ant-design/icons';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';

// 组件
import Header from './components/Header';
import FooterItem from './components/FooterItem';
import ProtectedRoute from './components/ProtectedRoute';


// 懒加载页面
const Home = lazy(() => import('./pages/Home'));
const Archive = lazy(() => import('./pages/Archive'));
const About = lazy(() => import('./pages/About'));
const Auth = lazy(() => import('./pages/Auth'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const WritePost = lazy(() => import('./pages/WritePost'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const Drafts = lazy(() => import('./pages/Drafts'));

// 自定义主题 - 清新可爱风格
const cuteTheme = {
  token: {
    colorPrimary: '#ff85c0',
    colorSuccess: '#95de64',
    colorWarning: '#ffd666',
    colorError: '#ff7875',
    colorInfo: '#36cfc9',
    borderRadius: 12,
    fontFamily: "'Comic Neue', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
  },
  components: {
    Button: {
      controlHeight: 40,
      borderRadius: 20,
    },
    Card: {
      borderRadius: 16,
      boxShadow: '0 8px 20px rgba(255, 133, 192, 0.1)',
    },
    Layout: {
      bodyBg: 'transparent',
    },
    Input: {
      borderRadius: 20,
    },
    Tag: {
      borderRadius: 20,
    },
  },
};

// 加载中组件
const LoadingSpinner = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    background: 'linear-gradient(135deg, #f9f7fe 0%, #f0f9ff 100%)'
  }}>
    <Spin 
      indicator={
        <LoadingOutlined 
          style={{ 
            fontSize: 48, 
            color: '#ff85c0',
            animation: 'spin 1s linear infinite'
          }} 
        />
      } 
    />
    <span style={{ color: '#ff85c0', fontSize: 16, fontWeight: 500 }}>
      加载中...
    </span>
  </div>
);

function App() {
  const { Content } = Layout;

  return (
    <ConfigProvider 
      theme={cuteTheme}
      getPopupContainer={trigger => trigger?.parentElement || document.body}
      motion={{ motion: false }}
    >
      <Router>
        <AntApp>
          <div className="app">
            <Layout style={{ background: 'transparent', minHeight: '100vh' }}>
              <Header />
              <Content className="main-content">
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    {/* 公开页面 */}
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/" element={<Home />} />
                    <Route path="/archive" element={<Archive />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/post/:id" element={<PostDetail />} />
                    <Route path="/user/:username" element={<UserProfile />} />

                    {/* 需要登录的页面 */}
                    <Route path="/write" element={
                      <ProtectedRoute>
                        <WritePost />
                      </ProtectedRoute>
                    } />
                    <Route path="/write/:id" element={
                      <ProtectedRoute>
                        <WritePost />
                      </ProtectedRoute>
                    } />
                    <Route path="/drafts" element={
                      <ProtectedRoute>
                        <Drafts />
                      </ProtectedRoute>
                    } />
                    
                    {/* 需要管理员的页面 */}
                    <Route path="/admin" element={
                      <ProtectedRoute requireAdmin={true} requireAuth={true}>
                        <AdminPage />
                      </ProtectedRoute>
                    } />
                    
                    {/* 需要特定角色的页面示例 */}
                    <Route path="/editor" element={
                      <ProtectedRoute allowedRoles={['admin', 'editor']}>
                        <WritePost />
                      </ProtectedRoute>
                    } />

                    {/* 查看自己资料 */}
                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <UserProfile />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </Content>
              <FooterItem />
            </Layout>
            
            {/* 返回顶部按钮 */}
            <FloatButton.BackTop
              visibilityHeight={300}
              style={{
                right: 40,
                bottom: 100,
                width: 50,
                height: 50,
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #ff85c0, #ff7875)',
              }}
              icon={<SmileOutlined style={{ color: '#fff', fontSize: 20 }} />}
            />
          </div>
        </AntApp>
      </Router>
    </ConfigProvider>
  );
}

export default App;